import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { makeStyles } from 'tss-react/mui'
import { useShopCart } from '../../hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { ProductType } from '../../types'
import { LineItem } from '../../models'
import ShopRetrofitBatteryExistingSingleProduct from './ShopRetrofitBatteryExistingSingleProduct'
import { TextField } from 'src/Libs/inputs/formik'
import ButtonQuantity from '../ShopProduct/ButtonQuantity'

interface FormInputType {
  serialNumber: string
}

interface ShopRetrofitBatterySingleProductModalProps {
  batteryId: string
  onClose: () => void
  place: number
}

const formValues: FormInputType = {
  serialNumber: '',
}

const useStyles = makeStyles()(theme => ({
  fieldContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))

const RETROFIT_BATTERY_MINIMUM_QUANTITY = 1
const SERIAL_NUMBER_LENGTH_12 = 12
const SERIAL_NUMBER_LENGTH_16 = 16

/*
 * This component shows a modal and allow user to add, edit or change quantity of a retrofit battery with a serial number.
 */
const ShopRetrofitBatterySingleProductModal: React.FC<ShopRetrofitBatterySingleProductModalProps> = ({ onClose, place, batteryId }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { enqueueSuccessStack } = useSnackbar()
  const { addRetrofitBatteryCart, getCheckoutProducts, changeProductCartQuantity, deleteProductCart } = useShopCart()
  const [existingBatteries, setExistingBatteries] = useState<LineItem[]>([])
  const [newBatteryQuantity, setNewBatteryQuantity] = useState<number>(RETROFIT_BATTERY_MINIMUM_QUANTITY)

  const handleBatteryChangeQuantity = async (productId: string, quantity: number): Promise<void> => {
    const result = await changeProductCartQuantity(productId, quantity)
    const filteredBateries = filterBatteries(result?.lineItems ?? [])
    setExistingBatteries(filteredBateries)
  }

  const handleBatteryDelete = async (productId: string): Promise<void> => {
    const result = await deleteProductCart(productId)
    const filteredBateries = filterBatteries(result?.lineItems ?? [])
    setExistingBatteries(filteredBateries)
  }
  const filterBatteries = (lineItems: LineItem[]): LineItem[] => {
    return lineItems.filter(product => {
      const productIsBatteryType = product.payload?.e3dc?.productType === ProductType.RetrofitBattery
      const matchProduct = batteryId === product.referencedId
      return productIsBatteryType && matchProduct
    })
  }

  useEffect(() => {
    getCheckoutProducts().then(result => {
      const filteredBateries = filterBatteries(result?.lineItems ?? [])
      setExistingBatteries(filteredBateries)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = (values: FormInputType): void => {
    addRetrofitBatteryCart(batteryId, newBatteryQuantity, values.serialNumber).then(result => {
      if (result) {
        return enqueueSuccessStack(t('shop.retrofit-battery-single-product-order.add-to-cart-success'))
      }
    })
    onClose()
  }

  const validationSchema = Yup.object().shape({
    serialNumber: Yup.string()
      .test(
        'len',
        t('validations.form.serial-number.is-invalid-length'),
        val => val?.length === SERIAL_NUMBER_LENGTH_12 || val?.length === SERIAL_NUMBER_LENGTH_16
      )
      .matches(/^\S*$/, t('validations.form.serial-number.no-spaces'))
      .required(t('validations.form.serial-number.is-required')),
  })

  return (
    <Box>
      <Formik initialValues={formValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ dirty, isValid, values }) => {
          return (
            <Dialog
              title={t('shop.retrofit-battery-single-product-order.retrofit-battery')}
              onClose={onClose}
              actions={{
                primary: {
                  disabled: !isValid || !dirty,
                  name: t('shop.retrofit-battery-single-product-order.add-battery-button'),
                  onClick: () => onSubmit(values),
                },
                secondary: {
                  name: t('common.close'),
                  onClick: () => onClose(),
                },
              }}
              maxWidth="sm"
              place={place}
            >
              <Typography variant="h3">{t('shop.retrofit-battery-single-product-order.enter-serial-number')}</Typography>
              {existingBatteries.map(battery => {
                return (
                  <ShopRetrofitBatteryExistingSingleProduct
                    key={battery.id}
                    batteryId={battery.id}
                    serialNumber={battery.payload.e3dc.serialNumber}
                    batteryQuantity={battery.quantity}
                    maxQuantity={battery.quantityInformation.maxPurchase}
                    minQuantity={battery.quantityInformation.minPurchase}
                    onBatteryChangeQuantity={handleBatteryChangeQuantity}
                    onBatteryDelete={handleBatteryDelete}
                  />
                )
              })}
              <Box className={classes.fieldContainer}>
                <TextField fullWidth label={t('shop.retrofit-battery-single-product-order.serial-number-input')} name={'serialNumber'} />
                <ButtonQuantity
                  quantity={newBatteryQuantity}
                  onQuantityDecrease={() => {
                    setNewBatteryQuantity(newBatteryQuantity - 1)
                  }}
                  onQuantityIncrease={() => {
                    setNewBatteryQuantity(newBatteryQuantity + 1)
                  }}
                  onQuantityChange={quantity => setNewBatteryQuantity(quantity)}
                  minQuantity={RETROFIT_BATTERY_MINIMUM_QUANTITY}
                />
              </Box>
            </Dialog>
          )
        }}
      </Formik>
    </Box>
  )
}

export default ShopRetrofitBatterySingleProductModal
